<template>
  <div>
    <div class="mb-5 print-section" v-for="(section, sIdx) in localBrief" :key="section.name || sIdx">
      <h3 class="title is-4 has-text-primary mb-2">{{ section.name }}</h3>
      <p class="content is-small mb-5 print-hidden section-description">
        {{ section.description }}
      </p>

      <!-- Asset Selection Section -->
      <div v-if="section.type === 'asset-selection'">
        <!-- Selected Assets Display -->
        <div v-if="selectedAssets.length > 0" class="block">
          <div class="level is-mobile mb-3">
            <div class="level-left">
              <div class="level-item">
                <p class="title is-6 mb-0">
                  Selected Assets ({{ selectedAssets.length }})
                </p>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <div class="field has-addons">
                  <div class="control">
                    <button
                      v-if="!disableFields"
                      class="button is-primary is-small"
                      @click="openAssetSelectionModal"
                    >
                      <span class="icon is-small">
                        <font-awesome-icon :icon="['fas', 'edit']" />
                      </span>
                      <span>Edit Selection</span>
                    </button>
                  </div>
                  <div class="control" v-if="showDownloadXml">
                    <button
                      class="button is-primary is-small"
                      @click="downloadXML"
                      :disabled="selectedAssets.length === 0"
                      :title="
                        selectedAssets.length === 0
                          ? 'No assets selected'
                          : 'Download EDL timeline for Premiere'
                      "
                    >
                      <span class="icon is-small">
                        <font-awesome-icon :icon="['fas', 'download']" />
                      </span>
                      <span>Download EDL</span>
                    </button>
                  </div>
                  <div class="control">
                    <button
                      class="button is-primary is-small"
                      @click="downloadAssets"
                      :disabled="selectedAssets.length === 0 || isDownloadingAssets"
                      :class="{ 'is-loading': isDownloadingAssets }"
                      :title="
                        selectedAssets.length === 0
                          ? 'No assets selected'
                          : 'Download the selected asset files'
                      "
                    >
                      <span class="icon is-small">
                        <font-awesome-icon :icon="['fas', 'download']" />
                      </span>
                      <span>Download Assets</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Client media review notice (contract gate active + client media selected) -->
          <div
            v-if="selectionHasClientMedia"
            class="notification is-info is-flex is-align-items-center py-2 px-3 mb-3 client-media-restriction-notice"
          >
            <span class="icon mr-2 has-text-info">
              <font-awesome-icon :icon="['fal', 'circle-info']" />
            </span>
            <span class="is-size-7">{{ clientMediaRestrictionNotice }}</span>
          </div>

          <div class="selected-assets-grid">
            <div
              v-for="asset in selectedAssets"
              :key="asset.id"
              class="selected-asset-item"
            >
              <MediaCard
                :item="asset"
                :is-selected="true"
                :is-multi-select-mode="true"
                :selection-order="getAssetSelectionOrder(asset)"
                @preview="handleAssetPreview"
              />
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="has-text-centered py-5">
          <span class="icon is-large has-text-grey-light mb-3">
            <font-awesome-icon :icon="['fal', 'folder-open']" size="2x" />
          </span>
          <p class="title is-6 has-text-grey">No Assets Selected</p>
          <p class="subtitle is-7 has-text-grey mb-4">
            Choose from your company's existing assets for this project.
          </p>
          <button
            v-if="!disableFields"
            class="button is-primary"
            @click="openAssetSelectionModal"
          >
            <span class="icon">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </span>
            <span>Select Assets</span>
          </button>
        </div>
      </div>

      <!-- Pricing Calculator Section -->
      <div v-else-if="section.type === 'pricing-calculator'">
        <PricingCalculatorSection
          v-if="getCalculatorConfig(section)"
          :calculator="getCalculatorConfig(section)"
          :model-value="getCalculatorSelections(section)?.values || {}"
          :disabled="disableFields"
          :readonly-summary="disableFields"
          :line-items="getCalculatorSelections(section)?.lineItems || []"
          :total="getCalculatorSelections(section)?.total || 0"
          @update:model-value="(newValues) => handleCalculatorUpdate(section, newValues)"
        />
      </div>

      <!-- Asset Selection Modal -->
      <div
        class="modal asset-selection-modal"
        :class="{ 'is-active': showAssetSelectionModal }"
      >
        <div class="modal-background" @click="closeAssetSelectionModal"></div>
        <div class="modal-card is-fullscreen">
          <header class="modal-card-head">
            <p class="modal-card-title">
              <font-awesome-icon :icon="['fal', 'folder-open']" class="mr-2" />
              Select Assets
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="closeAssetSelectionModal"
            ></button>
          </header>

          <section class="modal-card-body p-0">
            <div class="columns is-gapless">
              <!-- Filter Sidebar -->
              <div class="column is-narrow">
                <div class="filter-sidebar p-5">
                  <div class="level is-mobile mb-4">
                    <div class="level-left">
                      <div class="level-item">
                        <h2 class="title is-5 mb-0">
                          <span class="icon mr-2">
                            <font-awesome-icon :icon="['fal', 'filter']" />
                          </span>
                          Filter Assets
                        </h2>
                      </div>
                    </div>
                  </div>

                  <!-- Search -->
                  <div class="field mb-5">
                    <TaggedSearchInput
                      v-model="searchTags"
                      v-model:mode="searchMode"
                      :suggestions="searchSuggestions"
                      :is-loading="assetsLoading"
                      placeholder="Search tags, keywords, asset names..."
                      @search="onSearch"
                    />
                  </div>

                  <!-- Filter by Project -->
                  <div class="field mb-5">
                    <label class="label">Filter By Project</label>
                    <div class="control">
                      <SearchableSelect
                        v-model="filterProject"
                        :items="availableProjects"
                        value-field="id"
                        display-field="name"
                        placeholder="All Projects"
                        search-placeholder="Search projects..."
                        all-label="All Projects"
                        no-results-text="No projects found"
                        :show-all-option="true"
                        :multiple="true"
                      />
                    </div>
                  </div>

                  <!-- Type Filter -->
                  <div class="field mb-5" v-if="availableAssetTypes.length > 0">
                    <label class="label">Type</label>
                    <div class="type-filter-cards">
                      <div
                        v-for="assetType in availableAssetTypes"
                        :key="assetType"
                        class="type-card transition"
                        :class="{
                          'is-active': filterAssetType.includes(assetType),
                        }"
                        @click="toggleTypeFilter(assetType)"
                      >
                        <div class="type-card-content">
                          <span
                            class="type-icon transition"
                            :class="
                              filterAssetType.includes(assetType)
                                ? 'has-text-primary'
                                : 'has-text-grey-light'
                            "
                          >
                            <font-awesome-icon
                              :icon="
                                filterAssetType.includes(assetType)
                                  ? ['fas', getAssetTypeIcon(assetType)]
                                  : ['fal', getAssetTypeIcon(assetType)]
                              "
                            />
                          </span>
                          <span class="type-label">{{
                            getAssetTypeDisplayName(assetType)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Orientation Filter -->
                  <div class="field" v-if="availableAspectRatioGroups.length > 0">
                    <label class="label">Orientation</label>
                    <div class="aspect-ratio-filter-cards">
                      <!-- Horizontal -->
                      <div
                        v-if="availableAspectRatioGroups.includes('horizontal')"
                        class="aspect-ratio-card transition"
                        :class="{ 'is-active': filterAspectRatio.includes('horizontal') }"
                        @click="toggleAspectRatioGroup('horizontal')"
                      >
                        <div class="aspect-ratio-card-content">
                          <span
                            class="aspect-ratio-icon transition"
                            :class="
                              filterAspectRatio.includes('horizontal')
                                ? 'has-text-primary'
                                : 'has-text-grey-light'
                            "
                          >
                            <font-awesome-icon
                              :icon="[
                                filterAspectRatio.includes('horizontal') ? 'fas' : 'fal',
                                'arrows-left-right'
                              ]"
                            />
                          </span>
                          <span class="aspect-ratio-label">Horizontal</span>
                        </div>
                      </div>
                      
                      <!-- Vertical -->
                      <div
                        v-if="availableAspectRatioGroups.includes('vertical')"
                        class="aspect-ratio-card transition"
                        :class="{ 'is-active': filterAspectRatio.includes('vertical') }"
                        @click="toggleAspectRatioGroup('vertical')"
                      >
                        <div class="aspect-ratio-card-content">
                          <span
                            class="aspect-ratio-icon transition"
                            :class="
                              filterAspectRatio.includes('vertical')
                                ? 'has-text-primary'
                                : 'has-text-grey-light'
                            "
                          >
                            <font-awesome-icon
                              :icon="[
                                filterAspectRatio.includes('vertical') ? 'fas' : 'fal',
                                'arrows-up-down'
                              ]"
                            />
                          </span>
                          <span class="aspect-ratio-label">Vertical</span>
                        </div>
                      </div>
                      
                      <!-- Square -->
                      <div
                        v-if="availableAspectRatioGroups.includes('square')"
                        class="aspect-ratio-card transition"
                        :class="{ 'is-active': filterAspectRatio.includes('square') }"
                        @click="toggleAspectRatioGroup('square')"
                      >
                        <div class="aspect-ratio-card-content">
                          <span
                            class="aspect-ratio-icon transition"
                            :class="
                              filterAspectRatio.includes('square')
                                ? 'has-text-primary'
                                : 'has-text-grey-light'
                            "
                          >
                            <font-awesome-icon
                              :icon="[
                                filterAspectRatio.includes('square') ? 'fas' : 'fal',
                                'square'
                              ]"
                            />
                          </span>
                          <span class="aspect-ratio-label">Square</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Clear Filters Button -->
                  <div class="field mt-5" v-if="hasActiveFilters">
                    <button
                      class="button is-outlined is-fullwidth is-small is-primary transition"
                      @click="clearFiltersAndClose"
                    >
                      <span class="icon">
                        <font-awesome-icon :icon="['fal', 'refresh']" />
                      </span>
                      <span>Clear All Filters</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Main Content -->
              <div class="column">
                <div class="content p-5">
                  <!-- Selection Summary -->
                  <div class="notification is-primary is-light mb-4">
                    <div class="level is-mobile">
                      <div class="level-left">
                        <div class="level-item">
                          <strong
                            >{{ tempSelectedAssets.length }} asset{{
                              tempSelectedAssets.length === 1 ? "" : "s"
                            }}
                            selected</strong
                          >
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <button
                            class="button is-small is-text"
                            :disabled="tempSelectedAssets.length === 0"
                            @click="clearTempSelection"
                          >
                            Clear Selection
                          </button>
                          <button
                            class="button is-small is-primary ml-2"
                            :disabled="paginatedAvailableAssets.length === 0"
                            @click="selectAllVisible"
                          >
                            Select All Visible
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Results summary -->
                  <div class="block">
                    <p class="has-text-grey is-size-7 mb-4">
                      <span v-if="totalAssetPages > 1">
                        Showing {{ paginationInfo.start }}-{{
                          paginationInfo.end
                        }}
                        of {{ paginationInfo.total }} assets
                      </span>
                      <span v-else-if="paginationInfo.total > 0">
                        Showing {{ paginationInfo.total }} assets
                      </span>
                      <span v-else> No assets to display </span>
                      <span v-if="hasActiveFilters" class="has-text-primary">
                        (filtered from {{ paginationInfo.totalAll }} total)
                      </span>
                      <span
                        v-if="!hasActiveFilters && paginationInfo.totalAll > 0"
                        class="has-text-grey"
                      >
                        ({{ paginationInfo.totalAll }} total available)
                      </span>
                    </p>
                    <hr class="my-3" />
                  </div>

                  <!-- Client media review notice (contract gate active + client media selected) -->
                  <div
                    v-if="tempSelectionHasClientMedia"
                    class="notification is-info is-flex is-align-items-center py-2 px-3 mb-4 client-media-restriction-notice"
                  >
                    <span class="icon mr-2 has-text-info">
                      <font-awesome-icon :icon="['fal', 'circle-info']" />
                    </span>
                    <span class="is-size-7">{{ clientMediaRestrictionNotice }}</span>
                  </div>

                  <!-- Loading State -->
                  <div v-if="assetsLoading" class="has-text-centered py-6">
                    <LoadingSpinner
                      title="Loading Assets..."
                      subtitle="Fetching your media library"
                      size="3x"
                    />
                  </div>

                  <!-- No results for filters -->
                  <div
                    v-else-if="availableAssets.length === 0 && hasActiveFilters"
                    class="has-text-centered py-6"
                  >
                    <div class="filtered-empty-state">
                      <span class="icon is-large has-text-grey-light mb-4">
                        <font-awesome-icon
                          :icon="['fas', 'filter']"
                          size="2x"
                        />
                      </span>
                      <h4 class="title is-5 has-text-grey">
                        No Assets Match Your Filters
                      </h4>
                      <p class="has-text-grey mb-4">
                        Try adjusting your filter settings to see more assets
                      </p>
                      <p class="has-text-grey-light is-size-7 mb-4">
                        Total assets available:
                        {{ baseClientAssets.length }}
                      </p>
                      <button
                        class="button is-primary"
                        @click="clearFiltersAndClose"
                      >
                        <span class="icon">
                          <font-awesome-icon :icon="['fas', 'refresh']" />
                        </span>
                        <span>Clear Filters</span>
                      </button>
                    </div>
                  </div>

                  <!-- No assets at all -->
                  <div
                    v-else-if="
                      availableAssets.length === 0 &&
                      !hasActiveFilters &&
                      !assetsLoading
                    "
                    class="has-text-centered py-6"
                  >
                    <div class="empty-state">
                      <span class="icon is-large has-text-grey-light mb-4">
                        <font-awesome-icon
                          :icon="['fal', 'folder-open']"
                          size="2x"
                        />
                      </span>
                      <h4 class="title is-5 has-text-grey">
                        No Assets Available
                      </h4>
                      <p class="has-text-grey mb-4">
                        This company doesn't have any assets uploaded yet.
                      </p>
                      <p class="has-text-grey-light is-size-7">
                        Company ID: {{ props.companyId || "Not set" }}
                      </p>
                    </div>
                  </div>

                  <!-- Asset Grid -->
                  <div v-else-if="paginatedAvailableAssets.length > 0">
                    <div class="asset-selection-grid">
                      <div
                        class="asset-selection-item"
                        v-for="asset in paginatedAvailableAssets"
                        :key="asset.id"
                      >
                        <MediaCard
                          :item="asset"
                          :is-selected="isAssetTempSelected(asset)"
                          :is-multi-select-mode="true"
                          :selection-order="getAssetTempSelectionOrder(asset)"
                          @click="toggleTempAssetSelection(asset)"
                          @preview="handleAssetPreview"
                        />
                      </div>
                    </div>

                    <!-- Pagination Controls -->
                    <div v-if="totalAssetPages > 1" class="mt-6 pb-4">
                      <div
                        class="is-flex is-justify-content-space-between is-align-items-center"
                      >
                        <!-- Page Size Selector on Left -->
                        <div class="is-flex is-align-items-center">
                          <span class="has-text-grey is-size-7 mr-2"
                            >Show:</span
                          >
                          <div class="select is-small">
                            <select
                              v-model="assetsPerPage"
                              style="min-width: 60px"
                            >
                              <option :value="9">9</option>
                              <option :value="18">18</option>
                              <option :value="36">36</option>
                              <option :value="54">54</option>
                            </select>
                          </div>
                          <span class="has-text-grey is-size-7 ml-2"
                            >per page</span
                          >
                        </div>

                        <!-- Pagination on Right -->
                        <nav
                          class="pagination is-right is-small"
                          role="navigation"
                          aria-label="pagination"
                          style="margin: 0"
                        >
                          <ul class="pagination-list">
                            <!-- Previous arrow at start -->
                            <li>
                              <a
                                class="pagination-link is-primary"
                                :class="{
                                  'is-disabled': currentAssetPage === 1,
                                }"
                                @click.prevent="prevAssetPage"
                                :title="
                                  currentAssetPage === 1
                                    ? 'This is the first page'
                                    : 'Go to previous page'
                                "
                              >
                                <span class="icon is-small">
                                  <font-awesome-icon
                                    :icon="['fas', 'chevron-left']"
                                  />
                                </span>
                              </a>
                            </li>

                            <!-- First page -->
                            <li v-if="currentAssetPage > 3">
                              <a
                                class="pagination-link"
                                @click.prevent="goToAssetPage(1)"
                                :aria-label="`Goto page 1`"
                                >1</a
                              >
                            </li>
                            <li v-if="currentAssetPage > 4">
                              <span class="pagination-ellipsis">&hellip;</span>
                            </li>

                            <!-- Pages around current page -->
                            <li v-for="page in visibleAssetPages" :key="page">
                              <a
                                class="pagination-link"
                                :class="{
                                  'is-current': page === currentAssetPage,
                                }"
                                @click.prevent="goToAssetPage(page)"
                                :aria-label="
                                  page === currentAssetPage
                                    ? `Page ${page}`
                                    : `Goto page ${page}`
                                "
                                :aria-current="
                                  page === currentAssetPage ? 'page' : undefined
                                "
                              >
                                {{ page }}
                              </a>
                            </li>

                            <!-- Last page -->
                            <li v-if="currentAssetPage < totalAssetPages - 3">
                              <span class="pagination-ellipsis">&hellip;</span>
                            </li>
                            <li v-if="currentAssetPage < totalAssetPages - 2">
                              <a
                                class="pagination-link"
                                @click.prevent="goToAssetPage(totalAssetPages)"
                                :aria-label="`Goto page ${totalAssetPages}`"
                                >{{ totalAssetPages }}</a
                              >
                            </li>

                            <!-- Next arrow at end -->
                            <li>
                              <a
                                class="pagination-link is-primary"
                                :class="{
                                  'is-disabled':
                                    currentAssetPage === totalAssetPages,
                                }"
                                @click.prevent="nextAssetPage"
                                :title="
                                  currentAssetPage === totalAssetPages
                                    ? 'This is the last page'
                                    : 'Go to next page'
                                "
                              >
                                <span class="icon is-small">
                                  <font-awesome-icon
                                    :icon="['fas', 'chevron-right']"
                                  />
                                </span>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="has-text-centered py-6">
                    <span class="icon is-large has-text-grey-light mb-4">
                      <font-awesome-icon
                        :icon="['fal', 'folder-open']"
                        size="3x"
                      />
                    </span>
                    <h4 class="title is-6 has-text-grey">
                      No Assets Available
                    </h4>
                    <p class="subtitle is-7 has-text-grey">
                      No assets have been uploaded to this company yet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <footer class="modal-card-foot is-justify-content-flex-end">
            <button class="button is-text" @click="closeAssetSelectionModal">
              Cancel
            </button>
            <button class="button is-primary" @click="confirmAssetSelection">
              <span class="icon">
                <font-awesome-icon :icon="['fas', 'check']" />
              </span>
              <span>Confirm Selection ({{ tempSelectedAssets.length }})</span>
            </button>
          </footer>
        </div>
      </div>

      <!-- Regular Fields (only for standard sections) -->
      <template v-if="section.type !== 'asset-selection' && section.type !== 'pricing-calculator'">
        <div
          class="field my-5"
          v-for="(field, fIdx) in section.fields"
          :key="field.input?.id || fIdx"
          :id="field.id"
          :class="[
            field.class,
            { 'is-admin-only': field.admin },
            { 'is-hidden': !isAdmin && field.admin },
            { 'has-pending-changes': hasPendingChange(field.input?.id) },
          ]"
        >
        <label class="label" :for="field.input.id">
          {{ field.label }}
          <span v-if="field.input.required" class="has-text-danger">*</span>
          <span v-if="hasPendingChange(field.input?.id)" class="tag is-warning is-small ml-2">
            <span class="icon is-small">
              <font-awesome-icon :icon="['fas', 'clock']" />
            </span>
            <span>Pending Review</span>
          </span>
        </label>

        <div class="control">
          <input
            v-if="field.type == 'text'"
            type="text"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <input
            v-if="field.type == 'number'"
            type="number"
            class="input is-small print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            style="max-width: 8rem"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <textarea
            v-if="field.type == 'textarea'"
            type="text"
            class="textarea print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            rows="3"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          ></textarea>

          <!-- File Upload Field -->
          <div v-if="field.type == 'file'" class="print-hidden">
            <!-- Uploaded Files Display - Show FIRST so users see their uploads -->
            <div
              v-if="
                field.input.value &&
                (field.input.value.downloadURL ||
                  field.input.value.storagePath ||
                  (Array.isArray(field.input.value) &&
                    field.input.value.length > 0))
              "
              class="uploaded-files mb-3"
            >
              <div class="notification is-primary is-light">
                <div class="level is-mobile mb-2">
                  <div class="level-left">
                    <div class="level-item">
                      <strong>Uploaded Files:</strong>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <button
                        class="button is-small is-text"
                        @click="downloadAllFiles(field)"
                      >
                        <span class="icon is-small">
                          <font-awesome-icon icon="fa-solid fa-download" />
                        </span>
                        <span>Download All</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Single File -->
                <div
                  v-if="
                    !field.input.multiple &&
                    field.input.value &&
                    (field.input.value.downloadURL || field.input.value.storagePath)
                  "
                  class="uploaded-file-item"
                >
                  <div class="level is-mobile">
                    <div class="level-left">
                      <div class="level-item">
                        <span class="icon is-small mr-2">
                          <font-awesome-icon icon="fa-solid fa-file" />
                        </span>
                        <span class="is-size-7">{{
                          field.input.value.name
                        }}</span>
                        <span class="is-size-7 has-text-grey ml-2"
                          >({{ formatFileSize(field.input.value.size) }})</span
                        >
                      </div>
                    </div>
                    <div class="level-right">
                      <div class="level-item">
                        <button
                          class="button is-small is-text"
                          @click="downloadFile(field.input.value)"
                          title="Download file"
                        >
                          <span class="icon is-small">
                            <font-awesome-icon icon="fa-solid fa-download" />
                          </span>
                        </button>
                        <button
                          v-if="!disableFields"
                          class="button is-small is-text is-danger ml-1"
                          @click="removeFile(section, field)"
                          title="Remove file"
                        >
                          <span class="icon is-small">
                            <font-awesome-icon icon="fa-solid fa-times" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Multiple Files -->
                <div
                  v-if="
                    field.input.multiple && Array.isArray(field.input.value)
                  "
                  class="uploaded-files-list"
                >
                  <div
                    v-for="(file, index) in field.input.value"
                    :key="index"
                    class="uploaded-file-item mb-2"
                  >
                    <div class="level is-mobile">
                      <div class="level-left">
                        <div class="level-item">
                          <span class="icon is-small mr-2">
                            <font-awesome-icon icon="fa-solid fa-file" />
                          </span>
                          <span class="is-size-7">{{ file.name }}</span>
                          <span class="is-size-7 has-text-grey ml-2"
                            >({{ formatFileSize(file.size) }})</span
                          >
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <button
                            class="button is-small is-text"
                            @click="downloadFile(file)"
                            title="Download file"
                          >
                            <span class="icon is-small">
                              <font-awesome-icon icon="fa-solid fa-download" />
                            </span>
                          </button>
                          <button
                            v-if="!disableFields && !readonly"
                            class="button is-small is-text is-danger ml-1"
                            @click="removeFile(section, field, index)"
                            title="Remove file"
                          >
                            <span class="icon is-small">
                              <font-awesome-icon icon="fa-solid fa-times" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- File Upload Input - Show when not readonly AND under 5-file limit -->
            <div
              v-if="!readonly && (!field.input.multiple || getFileCount(field) < 5)"
              class="file has-name is-fullwidth mb-3"
            >
              <label class="file-label">
                <input
                  :id="field.input.id"
                  :name="field.input.id"
                  class="file-input"
                  type="file"
                  :disabled="disableFields"
                  :required="field.input.required && getFileCount(field) === 0"
                  :accept="field.input.accept || undefined"
                  :multiple="field.input.multiple || false"
                  @change="handleFileChange($event, field, section)"
                />
                <span class="file-cta">
                  <span class="file-icon">
                    <font-awesome-icon icon="fa-solid fa-upload" />
                  </span>
                  <span class="file-label">
                    {{
                      getFileCount(field) > 0
                        ? "Add more files..."
                        : field.input.multiple
                          ? "Choose files..."
                          : "Choose a file..."
                    }}
                  </span>
                </span>
              </label>
            </div>

            <!-- Size usage for multi-file fields -->
            <p
              v-if="!readonly && field.input.multiple && getFileCount(field) > 0"
              class="help has-text-grey mb-2"
            >
              {{ getFileCount(field) }} {{ getFileCount(field) === 1 ? "file" : "files" }} ·
              {{ formatFileSize(getFieldTotalSize(field)) }} of {{ formatFileSize(MAX_FIELD_BYTES) }}
            </p>

            <!-- Empty State for readonly mode when no files uploaded -->
            <div
              v-if="readonly && (!field.input.value ||
                     (Array.isArray(field.input.value) && field.input.value.length === 0) ||
                     (!Array.isArray(field.input.value) && !field.input.value.downloadURL && !field.input.value.storagePath))"
              class="notification is-light"
            >
              <div class="has-text-grey has-text-centered py-2">
                <span class="icon is-small">
                  <font-awesome-icon icon="fa-solid fa-file" />
                </span>
                <span class="ml-2">No files uploaded</span>
              </div>
            </div>

            <!-- Help Text - Only show when not readonly -->
            <div
              v-if="!readonly && (field.input.accept || field.input.maxSize)"
              class="help mt-2"
            >
              <div v-if="field.input.accept" class="help-item">
                <strong>Accepted types:</strong> {{ field.input.accept }}
              </div>
              <div v-if="field.input.maxSize" class="help-item">
                <strong>Max size:</strong>
                {{ formatFileSize(field.input.maxSize < 1024 ? field.input.maxSize * 1024 * 1024 : field.input.maxSize) }}
              </div>
            </div>
          </div>

          <!-- Checkbox Input -->
          <div v-if="field.type == 'checkbox'" class="print-hidden">
            <label
              v-for="option in field.input.options"
              :key="option.input.id"
              class="checkbox is-block mb-2"
            >
              <input
                type="checkbox"
                :id="option.input.id"
                :value="option.input.value"
                :disabled="disableFields"
                :checked="
                  getFieldValue(section, field).includes(option.input.value)
                "
                @change="
                  updateCheckboxValue(
                    section,
                    field,
                    option.input.value,
                    $event.target.checked
                  )
                "
              />
              {{ option.label }}
            </label>
          </div>

          <!-- Radio Buttons -->
          <div v-if="field.type == 'radio'" class="print-hidden">
            <label
              v-for="option in field.input.options"
              :key="option.value"
              class="radio is-block mb-2"
            >
              <input
                type="radio"
                :name="field.input.id"
                :value="option.input.value"
                :disabled="disableFields"
                :checked="getFieldValue(section, field) === option.input.value"
                @change="updateFieldValue(section, field, option.input.value)"
              />
              {{ option.label }}
            </label>
          </div>

          <!-- Select/Dropdown -->
          <div v-if="field.type == 'select'" class="select print-hidden">
            <select
              v-bind="field.input"
              :disabled="disableFields"
              :value="getFieldValue(section, field)"
              @change="updateFieldValue(section, field, $event.target.value)"
            >
              <option
                v-for="option in field.input.options"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Date Input -->
          <input
            v-if="field.type == 'date'"
            type="date"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <!-- Time Input -->
          <input
            v-if="field.type == 'time'"
            type="time"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <!-- Email Input -->
          <input
            v-if="field.type == 'email'"
            type="email"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <!-- URL Input -->
          <input
            v-if="field.type == 'url'"
            type="url"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />

          <!-- Tel Input -->
          <input
            v-if="field.type == 'tel'"
            type="tel"
            class="input print-hidden"
            v-bind="field.input"
            :disabled="disableFields"
            :value="getFieldValue(section, field)"
            @input="updateFieldValue(section, field, $event.target.value)"
          />
        </div>

        <!-- Help text as a collapsible example, beneath the field -->
        <details v-if="field.helptext" class="brief-help-example print-hidden">
          <summary>See example</summary>
          <p class="help">{{ field.helptext }}</p>
        </details>
        </div>
      </template>
    </div>

    <!-- Toast Message -->

    <!-- Video Preview Modal -->
    <VideoPreviewModal
      :show="showVideoPreviewModal"
      :asset="previewAsset"
      :show-navigation="previewAssetList.length > 1"
      :can-navigate-prev="previewAssetIndex > 0"
      :can-navigate-next="previewAssetIndex < previewAssetList.length - 1"
      :show-select-button="showAssetSelectionModal"
      :is-selected="previewAsset && isAssetTempSelected(previewAsset)"
      @close="closeVideoPreviewModal"
      @navigate="navigatePreview"
      @select="handlePreviewSelect"
    />
  </div>
</template>

<script setup>
import {
  computed,
  watch,
  ref,
  onMounted,
  inject,
} from "vue";
import { useClientAssetsStore } from "@/stores/clientAssetsStore";
import { logger } from "@/services/loggerService";
import {
  getAvailableAssetTypes,
  getAssetTypeIcon,
  getAssetTypeDisplayName,
  getOrientation,
  applyClientAssetRestrictions,
  isClientStoreAsset,
  CLIENT_MEDIA_RESTRICTION_NOTICE,
} from "@/utils/assetUtils";
import { downloadEDLFile } from "@/utils/xmlUtils";
import { downloadAssetsSequentially } from "@/utils/assetDownload";
import TaggedSearchInput from "@/components/common/TaggedSearchInput.vue";
import SearchableSelect from "@/components/common/SearchableSelect.vue";
import MediaCard from "@/components/common/MediaCard.vue";
import LoadingSpinner from "@/components/global/LoadingSpinner.vue";
import VideoPreviewModal from "@/components/common/VideoPreviewModal.vue";
import { createLibraryAsset } from "@/services/bunnyUploadService";
import { refreshBunnyUrl } from "@/services/bunnyUrlService";
import { LIBRARY_ASSET_TYPE, BUNNY_URL_INTENT } from "@/utils/bunnyConstants";
import { useToast } from '@/composables/useToast';
import { formatFileSize } from '@/utils/assetUtils';
import { PROJECT_STATUS } from '@/utils/projectUtils';
import PricingCalculatorSection from "@/components/common/PricingCalculatorSection.vue";
import { computeCalculatorTotals } from "@/services/pricingCalculatorService";

// Inject user for project context
inject("user", {});

// Store access
const clientAssetsStore = useClientAssetsStore();

// Initialize toast
const toast = useToast();

// Use the passed assets store or fall back to client store
const activeAssetsStore = computed(() => {
  return props.assetsStore || clientAssetsStore;
});

// Search normalization function - treats spaces and underscores the same
const normalizeForSearch = (text) => text?.toLowerCase().replace(/[_\s]+/g, ' ').trim() || '';

// Asset selection state
const filterAssetType = ref([]);
const filterProject = ref([]);
const filterAspectRatio = ref([]);
const searchTags = ref([]);
const searchMode = ref("any");
const currentAssetPage = ref(1);
const assetsPerPage = ref(18);
const selectedAssets = ref([]);
const assetsLoading = ref(false);

// Modal state
const showAssetSelectionModal = ref(false);
const tempSelectedAssets = ref([]);
// Video preview modal state
const showVideoPreviewModal = ref(false);
const previewAsset = ref(null);
const previewAssetList = ref([]);
const previewAssetIndex = ref(0);

// Note: The watcher for pre-selected assets is defined after updateAssetSelectionInBrief function
// to avoid ReferenceError when using immediate: true option

/**
 * @typedef {Object} BriefField
 * @property {string} id - Unique identifier for the field
 * @property {string} type - Field type (text, number, textarea, radio, checkbox, file, asset-selection)
 * @property {string} label - Display label for the field
 * @property {string} [helptext] - Optional help text
 * @property {boolean} [admin] - Whether this field is admin-only
 * @property {string} [class] - Additional CSS classes
 * @property {Object} input - Input configuration
 * @property {string} input.id - Input element ID
 * @property {boolean} input.required - Whether the field is required
 * @property {string} input.value - Current field value
 * @property {Array} [input.options] - Options for radio/checkbox fields
 */

/**
 * @typedef {Object} BriefSection
 * @property {string} name - Section name
 * @property {string} description - Section description
 * @property {string} type - Section type (normal or asset-selection)
 * @property {BriefField[]} fields - Array of fields in this section
 */

/**
 * @typedef {BriefSection[]} Brief
 */

const props = defineProps({
  /** The brief data to display and edit */
  brief: {
    type: Array,
    required: true,
    default: () => [],
    validator: (value) => {
      if (!value) return false;
      return (
        Array.isArray(value) &&
        value.every(
          (section) =>
            typeof section === "object" &&
            "name" in section &&
            "fields" in section
        )
      );
    },
  },
  /** Current project status */
  status: {
    type: String,
    required: true,
    default: PROJECT_STATUS.DRAFT,
    validator: (value) => {
      if (!value) return false;
      return [PROJECT_STATUS.DRAFT, PROJECT_STATUS.ACTIVE, PROJECT_STATUS.IN_REVIEW, "On Hold", PROJECT_STATUS.COMPLETED, "Cancelled"].includes(value);
    },
  },
  /** Whether the current user is an admin */
  isAdmin: {
    type: Boolean,
    required: true,
    default: false,
  },
  // Add company and project context for asset loading
  companyId: {
    type: String,
    default: "",
  },
  projectId: {
    type: String,
    default: "",
  },
  // Assets store to use (admin or client)
  assetsStore: {
    type: Object,
    default: null,
  },
  // Pending changes (for highlighting modified fields)
  pendingChanges: {
    type: Object,
    default: null,
  },
  // Whether to show the download XML button (hidden in wizard)
  showDownloadXml: {
    type: Boolean,
    default: true,
  },
  // Pre-selected assets to initialize with
  preSelectedAssets: {
    type: Array,
    default: () => [],
  },
  // Whether the component is in readonly mode
  readonly: {
    type: Boolean,
    default: false,
  },
  // Client-facing flow: always exclude client-transfer assets from the picker
  clientAssetContext: {
    type: Boolean,
    default: false,
  },
  // Contract gate active: show a review notice when client-store assets are selected
  flagClientMediaForReview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:brief"]);

// Local state for field values to prevent erasing during typing
const localFieldValues = ref(new Map());

// Work directly with the brief prop - no local copy needed
const localBrief = computed(() => {
  const brief = props.brief;
  if (!Array.isArray(brief)) return [];
  return brief.filter((section) => section && typeof section === "object");
});

// Initialize local field values when brief changes
watch(
  () => props.brief,
  (newBrief) => {
    if (newBrief && Array.isArray(newBrief)) {
      newBrief.forEach((section) => {
        if (section && section.fields && Array.isArray(section.fields)) {
          section.fields.forEach((field) => {
            const fieldKey = `${section.name}-${field.id}-${
              field.input?.id || "no-input-id"
            }`;
            if (
              field.input?.value !== undefined &&
              !localFieldValues.value.has(fieldKey)
            ) {
              localFieldValues.value.set(fieldKey, field.input.value);
            }
          });
        }
      });
    }
  },
  { immediate: true, deep: true }
);


// Compute whether fields should be disabled
const disableFields = computed(() => {
  if (props.isAdmin) {
    return false;
  }
  return props.status !== PROJECT_STATUS.DRAFT;
});

// Client-flow asset restrictions (no-op for admin pickers, which pass neither
// prop). Client-store assets stay selectable — the contract gate surfaces a
// review notice on selection instead of hiding them.
const clientAssetRestrictionOpts = computed(() => ({
  excludeTransfer: props.clientAssetContext,
}));

// Notice shown when the contract gate flags client-uploaded media for review
const clientMediaRestrictionNotice = CLIENT_MEDIA_RESTRICTION_NOTICE;

// Gate active and the in-modal (temp) selection includes client-uploaded media
const tempSelectionHasClientMedia = computed(
  () =>
    props.flagClientMediaForReview &&
    tempSelectedAssets.value.some(isClientStoreAsset)
);

// Gate active and the confirmed selection includes client-uploaded media
const selectionHasClientMedia = computed(
  () =>
    props.flagClientMediaForReview && selectedAssets.value.some(isClientStoreAsset)
);

// Asset-related computed properties
const availableAssets = computed(() => {
  const store = activeAssetsStore.value;
  if (!store) return [];

  let assets = [];

  // Duck-type the active store: prefer per-company getter, fall back to flat items.
  // Works for the admin store, the client store, and the orchestrator wrapper from AdminEditProject.
  if (typeof store.getAssetsForCompany === "function" && props.companyId) {
    assets = store.getAssetsForCompany(props.companyId) || [];
  }
  if ((!assets || assets.length === 0) && Array.isArray(store.items)) {
    assets = store.items;
  }

  if (!Array.isArray(assets) || assets.length === 0) {
    return [];
  }

  // Drop client-transfer (always, in client flows) and client-store (when gated)
  assets = applyClientAssetRestrictions(assets, clientAssetRestrictionOpts.value);

  // Apply search filter with tagged search
  if (searchTags.value.length > 0) {
    assets = assets.filter((item) => {
      const searchTerms = searchTags.value.map((tag) => normalizeForSearch(tag));

      const matchesName = item.name
        ? searchTerms.some((term) => normalizeForSearch(item.name).includes(term))
        : false;

      const matchesAssetType = item.assetType
        ? searchTerms.some((term) =>
            normalizeForSearch(item.assetType).includes(term)
          )
        : false;

      const matchesTags =
        item.tags && Array.isArray(item.tags)
          ? searchTerms.some((term) =>
              item.tags.some((tag) => normalizeForSearch(tag).includes(term))
            )
          : false;

      const itemMatches = [matchesName, matchesAssetType, matchesTags];

      // Apply search mode logic
      if (searchMode.value === "all") {
        // All search terms must match somewhere in the item
        return searchTerms.every((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      } else {
        // Any search term matches (default behavior)
        return itemMatches.some(Boolean);
      }
    });
  }

  // Apply asset type filter
  if (filterAssetType.value.length > 0) {
    assets = assets.filter((item) =>
      filterAssetType.value.some(
        (type) => item.assetType?.toLowerCase() === type.toLowerCase()
      )
    );
  }

  // Apply project filter
  if (filterProject.value.length > 0) {
    assets = assets.filter((item) =>
      filterProject.value.includes(item.project)
    );
  }

  // Apply aspect ratio filter (by orientation groups)
  if (filterAspectRatio.value.length > 0) {
    assets = assets.filter((item) => {
      if (!item.aspectRatio) return false;
      return filterAspectRatio.value.includes(getOrientation(item.aspectRatio));
    });
  }

  // Sort by upload date (newest first)
  return assets.sort((a, b) => {
    const dateA = new Date(a.uploadDate || 0);
    const dateB = new Date(b.uploadDate || 0);
    return dateB - dateA;
  });
});

// Client store items with client-flow restrictions applied — base list for
// facet computeds and counts so gated assets don't leak through suggestions
const baseClientAssets = computed(() =>
  applyClientAssetRestrictions(
    Array.isArray(clientAssetsStore.items) ? clientAssetsStore.items : [],
    clientAssetRestrictionOpts.value
  )
);

// Search suggestions from asset names and tags
const searchSuggestions = computed(() => {
  const suggestionMap = new Map();

  if (baseClientAssets.value.length === 0) {
    return [];
  }

  baseClientAssets.value.forEach((asset) => {
    // Add asset names
    if (asset.name && asset.name.trim()) {
      const value = asset.name.trim();
      if (!suggestionMap.has(value)) {
        suggestionMap.set(value, {
          value,
          type: "asset",
          assetType: asset.assetType || "unknown",
        });
      }
    }

    // Add tags
    if (asset.tags && Array.isArray(asset.tags)) {
      asset.tags.forEach((tag) => {
        if (tag && typeof tag === "string" && tag.trim()) {
          const value = tag.trim();
          if (!suggestionMap.has(value)) {
            suggestionMap.set(value, {
              value,
              type: "tag",
            });
          }
        }
      });
    }
  });

  // Convert Map to Array and sort
  return Array.from(suggestionMap.values()).sort((a, b) => {
    // Sort by type first (tags before assets), then alphabetically
    if (a.type !== b.type) {
      return a.type === "tag" ? -1 : 1;
    }
    return a.value.localeCompare(b.value);
  });
});

// Extract unique projects from currently filtered assets
const availableProjects = computed(() => {
  if (baseClientAssets.value.length === 0) {
    return [];
  }

  // Get assets filtered by search and type (but not project filter to avoid circular filtering)
  let assetsForProjectFilter = baseClientAssets.value;

  // Apply search filter
  if (searchTags.value.length > 0) {
    assetsForProjectFilter = assetsForProjectFilter.filter((item) => {
      const searchTerms = searchTags.value.map((tag) => normalizeForSearch(tag));

      if (searchMode.value === "all") {
        return searchTerms.every((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      } else {
        return searchTerms.some((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      }
    });
  }

  // Apply asset type filter
  if (filterAssetType.value.length > 0) {
    assetsForProjectFilter = assetsForProjectFilter.filter((item) =>
      filterAssetType.value.some(
        (type) => item.assetType?.toLowerCase() === type.toLowerCase()
      )
    );
  }

  // Apply aspect ratio filter (by orientation groups)
  if (filterAspectRatio.value.length > 0) {
    assetsForProjectFilter = assetsForProjectFilter.filter((item) => {
      if (!item.aspectRatio) return false;
      return filterAspectRatio.value.includes(getOrientation(item.aspectRatio));
    });
  }

  const uniqueProjects = new Map();
  assetsForProjectFilter.forEach((asset) => {
    if (asset.project && asset.projectName) {
      uniqueProjects.set(asset.project, {
        id: asset.project,
        name: asset.projectName,
      });
    }
  });

  return Array.from(uniqueProjects.values()).sort((a, b) =>
    (a?.name || "").localeCompare(b?.name || "")
  );
});

// Extract unique aspect ratios from currently filtered assets
const availableAspectRatioGroups = computed(() => {
  if (baseClientAssets.value.length === 0) {
    return [];
  }

  // Get assets filtered by search, project, and type (but not aspect ratio filter to avoid circular filtering)
  let assetsForRatioFilter = baseClientAssets.value;

  // Apply search filter
  if (searchTags.value.length > 0) {
    assetsForRatioFilter = assetsForRatioFilter.filter((item) => {
      const searchTerms = searchTags.value.map((tag) => normalizeForSearch(tag));

      if (searchMode.value === "all") {
        return searchTerms.every((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      } else {
        return searchTerms.some((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      }
    });
  }

  // Apply asset type filter
  if (filterAssetType.value.length > 0) {
    assetsForRatioFilter = assetsForRatioFilter.filter((item) =>
      filterAssetType.value.some(
        (type) => item.assetType?.toLowerCase() === type.toLowerCase()
      )
    );
  }

  // Apply project filter
  if (filterProject.value.length > 0) {
    assetsForRatioFilter = assetsForRatioFilter.filter((item) =>
      filterProject.value.includes(item.project)
    );
  }

  // Determine which aspect ratio groups exist
  const orientations = new Set();
  assetsForRatioFilter.forEach((a) => {
    if (a.aspectRatio) {
      const o = getOrientation(a.aspectRatio);
      if (o) orientations.add(o);
    }
  });
  return ['horizontal', 'vertical', 'square'].filter((o) => orientations.has(o));
});

// Check if there are active filters
const hasActiveFilters = computed(() => {
  return !!(
    searchTags.value.length > 0 ||
    filterAssetType.value.length > 0 ||
    filterProject.value.length > 0 ||
    filterAspectRatio.value.length > 0
  );
});

// Asset-related computed properties
const availableAssetTypes = computed(() => {
  if (baseClientAssets.value.length === 0) {
    return [];
  }

  // Get asset types from all company assets for filter availability
  let assetsForTypeFilter = baseClientAssets.value;

  // Apply search filter
  if (searchTags.value.length > 0) {
    assetsForTypeFilter = assetsForTypeFilter.filter((item) => {
      const searchTerms = searchTags.value.map((tag) => normalizeForSearch(tag));

      if (searchMode.value === "all") {
        return searchTerms.every((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      } else {
        return searchTerms.some((term) => {
          return (
            normalizeForSearch(item.name).includes(term) ||
            normalizeForSearch(item.assetType).includes(term) ||
            item.tags?.some((tag) => normalizeForSearch(tag).includes(term))
          );
        });
      }
    });
  }

  // Apply project filter
  if (filterProject.value.length > 0) {
    assetsForTypeFilter = assetsForTypeFilter.filter((item) =>
      filterProject.value.includes(item.project)
    );
  }

  // Apply aspect ratio filter (by orientation groups)
  if (filterAspectRatio.value.length > 0) {
    assetsForTypeFilter = assetsForTypeFilter.filter((item) => {
      if (!item.aspectRatio) return false;
      return filterAspectRatio.value.includes(getOrientation(item.aspectRatio));
    });
  }

  return getAvailableAssetTypes(assetsForTypeFilter);
});

const totalAssetPages = computed(() => {
  return Math.ceil(availableAssets.value.length / assetsPerPage.value);
});

const paginatedAvailableAssets = computed(() => {
  const start = (currentAssetPage.value - 1) * assetsPerPage.value;
  const end = start + assetsPerPage.value;
  return availableAssets.value.slice(start, end);
});

// Pagination info for display
const paginationInfo = computed(() => {
  const start = (currentAssetPage.value - 1) * assetsPerPage.value + 1;
  const end = Math.min(
    currentAssetPage.value * assetsPerPage.value,
    availableAssets.value.length
  );
  return {
    start,
    end,
    total: availableAssets.value.length,
    totalAll: baseClientAssets.value.length,
  };
});

// Visible pages for pagination
const visibleAssetPages = computed(() => {
  const pages = [];
  const current = currentAssetPage.value;
  const total = totalAssetPages.value;

  // Show up to 5 pages around current page
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

// Check if a field has pending changes
const hasPendingChange = (fieldId) => {
  if (!fieldId || !props.pendingChanges) return false;
  return !!props.pendingChanges[fieldId];
};

// Asset selection methods
const isAssetTempSelected = (asset) => {
  return tempSelectedAssets.value.some((selected) => selected.id === asset.id);
};

const getAssetTempSelectionOrder = (asset) => {
  const index = tempSelectedAssets.value.findIndex((selected) => selected.id === asset.id);
  return index > -1 ? index + 1 : null;
};

const getAssetSelectionOrder = (asset) => {
  const index = selectedAssets.value.findIndex((selected) => selected.id === asset.id);
  return index > -1 ? index + 1 : null;
};

const _toggleAssetSelection = (asset) => {
  if (disableFields.value) return;

  const index = selectedAssets.value.findIndex(
    (selected) => selected.id === asset.id
  );
  if (index > -1) {
    selectedAssets.value.splice(index, 1);
  } else {
    selectedAssets.value.push(asset);
  }

  // Update the brief data
  updateAssetSelectionInBrief();
};

const toggleTempAssetSelection = (asset) => {
  const index = tempSelectedAssets.value.findIndex(
    (selected) => selected.id === asset.id
  );
  if (index > -1) {
    tempSelectedAssets.value.splice(index, 1);
  } else {
    tempSelectedAssets.value.push(asset);
  }
};

// Filter handlers
const toggleTypeFilter = (type) => {
  const index = filterAssetType.value.indexOf(type);
  if (index > -1) {
    filterAssetType.value.splice(index, 1);
  } else {
    filterAssetType.value.push(type);
  }
  currentAssetPage.value = 1; // Reset pagination when filter changes
};

const toggleAspectRatioGroup = (group) => {
  const index = filterAspectRatio.value.indexOf(group);
  if (index > -1) {
    filterAspectRatio.value.splice(index, 1);
  } else {
    filterAspectRatio.value.push(group);
  }
  
  logger.info("[ProjectBrief] Toggled aspect ratio group filter", {
    group,
    isSelected: filterAspectRatio.value.includes(group),
    selectedGroups: filterAspectRatio.value,
  });
  
  currentAssetPage.value = 1; // Reset pagination when filter changes
};

const clearFilters = () => {
  searchTags.value = [];
  searchMode.value = "any";
  filterAssetType.value = [];
  filterProject.value = [];
  filterAspectRatio.value = [];
  currentAssetPage.value = 1; // Reset pagination when clearing filters
};

// Search handler
const onSearch = (_searchData) => {
  currentAssetPage.value = 1; // Reset pagination when search changes
};

// Modal methods
const openAssetSelectionModal = async () => {
  if (disableFields.value) return;

  // Ensure assets are loaded before opening the modal.
  // Use availableAssets (the resolved list) rather than poking at clientAssetsStore directly,
  // so we honor whichever store/wrapper was passed in via props.assetsStore.
  if (props.companyId && availableAssets.value.length === 0) {
    try {
      assetsLoading.value = true;
      const store = activeAssetsStore.value;
      if (store && typeof store.loadAssetsForCompany === "function") {
        await store.loadAssetsForCompany(props.companyId);
      } else if (store && typeof store.init === "function") {
        await store.init(props.companyId);
      } else {
        await loadAssetsForCompany(props.companyId);
      }
    } catch (error) {
      logger.error("[ProjectBrief] Failed to load assets for modal:", {
        error: error.message,
        companyId: props.companyId,
      });
    } finally {
      assetsLoading.value = false;
    }
  }

  // Initialize temp selection with current selection
  tempSelectedAssets.value = [...selectedAssets.value];

  // Reset pagination but keep reasonable defaults
  currentAssetPage.value = 1;

  showAssetSelectionModal.value = true;
};

const closeAssetSelectionModal = () => {
  showAssetSelectionModal.value = false;
  // Reset temp selection
  tempSelectedAssets.value = [];
};

// Add method to explicitly clear filters if needed
const clearFiltersAndClose = () => {
  clearFilters();
  closeAssetSelectionModal();
};

const confirmAssetSelection = () => {
  // Apply temp selection to actual selection
  selectedAssets.value = [...tempSelectedAssets.value];

  updateAssetSelectionInBrief();

  closeAssetSelectionModal();
};

const selectAllVisible = () => {
  // Add all visible assets to temp selection if not already selected
  paginatedAvailableAssets.value.forEach((asset) => {
    if (!isAssetTempSelected(asset)) {
      tempSelectedAssets.value.push(asset);
    }
  });
};

const clearTempSelection = () => {
  tempSelectedAssets.value = [];
};

// Video preview handlers
const handleAssetPreview = (asset) => {
  logger.debug("[ProjectBrief] Handling asset preview", {
    assetId: asset.id,
    name: asset.name,
  });

  // Determine which list of assets to use for navigation
  if (showAssetSelectionModal.value) {
    // Preview from selection modal - use all available assets for navigation
    const videoAssets = availableAssets.value.filter(
      (a) => a.assetType === "videos" || a.type?.startsWith("video/")
    );
    previewAssetList.value = videoAssets;
    previewAssetIndex.value = videoAssets.findIndex((a) => a.id === asset.id);
  } else {
    // Preview from selected assets
    const videoAssets = selectedAssets.value.filter(
      (a) => a.assetType === "videos" || a.type?.startsWith("video/")
    );
    previewAssetList.value = videoAssets;
    previewAssetIndex.value = videoAssets.findIndex((a) => a.id === asset.id);
  }

  previewAsset.value = asset;
  showVideoPreviewModal.value = true;
};

const closeVideoPreviewModal = () => {
  showVideoPreviewModal.value = false;
  previewAsset.value = null;
  previewAssetList.value = [];
  previewAssetIndex.value = 0;
};

const navigatePreview = (direction) => {
  const newIndex = previewAssetIndex.value + direction;
  if (newIndex >= 0 && newIndex < previewAssetList.value.length) {
    previewAssetIndex.value = newIndex;
    previewAsset.value = previewAssetList.value[newIndex];
  }
};

const handlePreviewSelect = (asset) => {
  if (showAssetSelectionModal.value) {
    // We're in the selection modal - toggle temp selection
    toggleTempAssetSelection(asset);
  }
};

// Pagination methods
const goToAssetPage = (page) => {
  if (page >= 1 && page <= totalAssetPages.value) {
    currentAssetPage.value = page;
  }
};

const nextAssetPage = () => {
  if (currentAssetPage.value < totalAssetPages.value) {
    goToAssetPage(currentAssetPage.value + 1);
  }
};

const prevAssetPage = () => {
  if (currentAssetPage.value > 1) {
    goToAssetPage(currentAssetPage.value - 1);
  }
};

// Update asset selection in brief data
const updateAssetSelectionInBrief = () => {
  // Get the original brief data (not the computed one with defaults)
  const originalBrief = props.brief || [];

  // Find asset selection section and update its data
  const assetSection = originalBrief.find(
    (section) => section.type === "asset-selection"
  );

  if (assetSection) {
    // Create a new brief with updated asset selection
    const updatedBrief = originalBrief.map((section) => {
      if (section.type === "asset-selection") {
        return {
          ...section,
          selectedAssetIds: selectedAssets.value.map((asset) => asset.id),
        };
      }
      return section;
    });

    // Emit update
    emit("update:brief", updatedBrief);
  }
};

// Pricing calculator helpers (defensive dual-shape reads, mirroring asset-selection)
const getCalculatorConfig = (section) => {
  return section?.calculator || section?._custom?.value?.calculator || null;
};

const getCalculatorSelections = (section) => {
  return section?.selections || section?._custom?.value?.selections || null;
};

// Update calculator selections in brief data (values + recomputed totals in one emit)
const handleCalculatorUpdate = (targetSection, newValues) => {
  const calculator = getCalculatorConfig(targetSection);
  if (!calculator) return;

  const totals = computeCalculatorTotals(calculator, newValues);
  const originalBrief = props.brief || [];

  const updatedBrief = originalBrief.map((section) => {
    if (section.type === "pricing-calculator" && section.id === targetSection.id) {
      return {
        ...section,
        selections: {
          ...(section.selections || {}),
          values: newValues,
          lineItems: totals.lineItems,
          total: totals.total,
          currency: totals.currency,
          computedAt: new Date().toISOString(),
        },
      };
    }
    return section;
  });

  emit("update:brief", updatedBrief);
};

// Watch for pre-selected assets prop changes
watch(
  () => props.preSelectedAssets,
  (newPreSelectedAssets) => {
    if (newPreSelectedAssets && newPreSelectedAssets.length > 0) {
      logger.info("[ProjectBrief] Pre-selected assets prop changed", {
        count: newPreSelectedAssets.length,
      });
      selectedAssets.value = [...newPreSelectedAssets];
      updateAssetSelectionInBrief();
    }
  },
  { immediate: true }
);

// Load assets for the company
const loadAssetsForCompany = async (newCompanyId) => {
  if (!newCompanyId) return;

  try {
    assetsLoading.value = true;
    await clientAssetsStore.init(newCompanyId);
  } catch (error) {
    logger.error("[ProjectBrief] Failed to load assets:", {
      error: error.message,
      companyId: newCompanyId,
    });
  } finally {
    assetsLoading.value = false;
  }
};

// Track if we're already loading assets to prevent duplicate calls
const isLoadingAssets = ref(false);

// Load assets from IDs (convert IDs back to full asset objects)
const loadAssetsFromIds = async (assetIds) => {
  // Prevent duplicate simultaneous calls
  if (isLoadingAssets.value) {
    logger.info(
      "[ProjectBrief] Already loading assets, skipping duplicate call"
    );
    return;
  }

  logger.debug("[ProjectBrief] loadAssetsFromIds called", {
    assetIdsCount: assetIds?.length || 0,
    companyId: props.companyId,
  });

  if (!assetIds || !Array.isArray(assetIds) || assetIds.length === 0) {
    selectedAssets.value = [];
    return;
  }

  isLoadingAssets.value = true;

  // Don't try to load assets if we don't have a company ID yet
  if (!props.companyId) {
    logger.warn("[ProjectBrief] No company ID, cannot load assets");
    selectedAssets.value = [];
    return;
  }

  // If assets aren't loaded yet and we have a company ID, try to load them first
  if (
    availableAssets.value.length === 0 &&
    props.companyId &&
    activeAssetsStore.value
  ) {
    try {
      // Duck-type the active store: admin store exposes loadAssetsForCompany,
      // client store exposes init, orchestrator wrapper exposes neither
      // (parent loads assets for it, so we just skip).
      const store = activeAssetsStore.value;
      if (typeof store.loadAssetsForCompany === "function") {
        await store.loadAssetsForCompany(props.companyId);
      } else if (typeof store.init === "function") {
        await store.init(props.companyId);
      }
    } catch (error) {
      logger.error(
        "[ProjectBrief] Failed to load assets before finding selected assets",
        {
          error: error.message,
          companyId: props.companyId,
        }
      );
      // Continue anyway - maybe some assets are already available
    }
  }

  // Find assets from the store by their IDs
  const foundAssets = assetIds
    .map((assetId) =>
      availableAssets.value.find((asset) => asset.id === assetId)
    )
    .filter((asset) => asset !== undefined); // Remove any assets that weren't found

  selectedAssets.value = foundAssets;

  // Log any missing assets
  const foundIds = foundAssets.map((asset) => asset.id);
  const missingIds = assetIds.filter((id) => !foundIds.includes(id));

  if (missingIds.length > 0) {
    logger.warn("[ProjectBrief] Some selected assets not found in store", {
      missingAssetIds: missingIds,
      foundCount: foundAssets.length,
      requestedCount: assetIds.length,
      storeItemsCount: availableAssets.value.length,
    });
  }

  // Clear loading flag
  isLoadingAssets.value = false;
};

// Watch for page size changes to reset pagination
watch(assetsPerPage, () => {
  currentAssetPage.value = 1;
});

// Watch for companyId changes to retry asset loading if needed
watch(
  () => props.companyId,
  async (newCompanyId) => {
    logger.info("[ProjectBrief] Company ID changed", {
      newCompanyId,
      hasBrief: !!props.brief,
      briefLength: props.brief?.length || 0,
      hasPreSelectedAssets: props.preSelectedAssets?.length > 0,
    });

    // Skip if we have pre-selected assets (they're already loaded)
    if (props.preSelectedAssets && props.preSelectedAssets.length > 0) {
      logger.info(
        "[ProjectBrief] Using pre-selected assets, skipping ID-based loading"
      );
      return;
    }

    if (newCompanyId && props.brief && props.brief.length > 0) {
      const assetSection = props.brief.find((section) => {
        // Check both regular format and reactive/custom format
        return (
          section.type === "asset-selection" ||
          section._custom?.value?.type === "asset-selection"
        );
      });

      // Extract selectedAssetIds from either format
      let selectedAssetIds = [];
      if (assetSection) {
        if (assetSection.selectedAssetIds) {
          selectedAssetIds = assetSection.selectedAssetIds;
        } else if (assetSection._custom?.value?.selectedAssetIds) {
          selectedAssetIds = assetSection._custom.value.selectedAssetIds;
        }
      }

      logger.info(
        "[ProjectBrief] Checking for asset section on company change",
        {
          hasAssetSection: !!assetSection,
          hasCustomFormat: !!assetSection?._custom,
          selectedAssetIds,
          currentSelectedAssetsCount: selectedAssets.value.length,
        }
      );

      if (selectedAssetIds.length > 0 && selectedAssets.value.length === 0) {
        await loadAssetsFromIds(selectedAssetIds);
      }
    }
  }
);

// Reset pagination when filters change
watch(
  [searchTags, searchMode, filterAssetType, filterProject, filterAspectRatio],
  () => {
    currentAssetPage.value = 1;
  }
);

// Watch for assets store changes to reload selected assets from IDs
watch(
  () => clientAssetsStore.items,
  (newAssets) => {
    if (newAssets && newAssets.length > 0) {
      // Check if we have asset IDs to reload
      const assetSection = props.brief?.find((section) => {
        // Check both regular format and reactive/custom format
        return (
          section.type === "asset-selection" ||
          section._custom?.value?.type === "asset-selection"
        );
      });

      // Extract selectedAssetIds from either format
      let selectedAssetIds = [];
      if (assetSection) {
        if (assetSection.selectedAssetIds) {
          selectedAssetIds = assetSection.selectedAssetIds;
        } else if (assetSection._custom?.value?.selectedAssetIds) {
          selectedAssetIds = assetSection._custom.value.selectedAssetIds;
        }
      }

      if (selectedAssetIds.length > 0) {
        // Only reload if we don't already have the right number of selected assets
        if (selectedAssets.value.length !== selectedAssetIds.length) {
          logger.info("[ProjectBrief] Reloading assets after store update", {
            expectedCount: selectedAssetIds.length,
            currentCount: selectedAssets.value.length,
          });
          (async () => {
            await loadAssetsFromIds(selectedAssetIds);
          })();
        }
      }
    }
  },
  { immediate: false, deep: false }
);

// Watch for company/project changes to load assets
watch(
  () => props.companyId,
  (newCompanyId) => {
    if (newCompanyId) {
      loadAssetsForCompany(newCompanyId);
    }
  },
  { immediate: true }
);

// File handling for regular file inputs
const handleFileChange = async (event, field, section) => {
  const files = event.target.files;
  if (!files || files.length === 0) {
    return;
  }

  if (!props.companyId) {
    showToast("Company ID is required for file upload", "danger");
    event.target.value = "";
    return;
  }

  // Get existing files for multi-file fields
  const existingFiles =
    field.input.multiple && Array.isArray(field.input.value)
      ? field.input.value
      : [];

  try {
    const fileArray = Array.from(files);

    // Per-field total size cap (existing + new)
    const incomingBytes = fileArray.reduce((sum, f) => sum + (f.size || 0), 0);
    if (getFieldTotalSize(field) + incomingBytes > MAX_FIELD_BYTES) {
      showToast(
        `Total attachments for this field can't exceed ${formatFileSize(MAX_FIELD_BYTES)}`,
        "warning"
      );
      event.target.value = "";
      return;
    }

    const uploadPromises = fileArray.map(async (file) => {
      // Validate file size if maxSize is specified
      if (field.input.maxSize) {
        const maxSizeBytes =
          field.input.maxSize < 1024
            ? field.input.maxSize * 1024 * 1024 // Convert MB to bytes
            : field.input.maxSize; // Already in bytes

        if (file.size > maxSizeBytes) {
          throw new Error(
            `File ${file.name} exceeds maximum size of ${formatFileSize(maxSizeBytes)}`
          );
        }
      }

      // Validate file type if accept is specified
      if (field.input.accept) {
        const acceptedTypes = field.input.accept
          .split(",")
          .map((type) => type.trim());
        const fileExtension = "." + file.name.split(".").pop().toLowerCase();
        const fileType = file.type;

        const isAccepted = acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith(".")) {
            return fileExtension === acceptedType.toLowerCase();
          } else if (acceptedType.includes("/*")) {
            const baseType = acceptedType.split("/")[0];
            return fileType.startsWith(baseType + "/");
          } else {
            return fileType === acceptedType;
          }
        });

        if (!isAccepted) {
          throw new Error(`File ${file.name} is not an accepted file type`);
        }
      }

      const { assetId, url, bunnyResult } = await createLibraryAsset({
        file,
        companyId: props.companyId,
        companyName: props.companyName || '',
        assetType: LIBRARY_ASSET_TYPE.BRIEF_ATTACHMENT,
        tags: ['brief'],
        project: props.projectId || '',
        projectName: props.projectName || '',
        urlIntent: BUNNY_URL_INTENT.DOWNLOAD,
      });

      return {
        name: file.name,
        size: file.size,
        type: file.type,
        downloadURL: url,
        // Video brief-attachments come back as bunnyStream (no bunnyStorage),
        // so guard the path access — the asset is identified by assetId anyway.
        storagePath: bunnyResult?.bunnyStorage?.path || null,
        assetId,
        uploadedAt: new Date().toISOString(),
      };
    });

    const uploadedFiles = await Promise.all(uploadPromises);

    // Update field value - APPEND for multiple files, replace for single.
    // Emitted, not written back onto the prop — see removeFile's note.
    const newValue = field.input.multiple
      ? [...existingFiles, ...uploadedFiles]
      : uploadedFiles[0];

    // Update the brief data
    updateFieldValue(section, field, newValue);

    // Reset file input so same file can be re-selected
    event.target.value = "";

    showToast(
      `Successfully uploaded ${uploadedFiles.length} file(s)`,
      "success"
    );
  } catch (error) {
    // An over-limit or wrong-type file is the user's file, not a system fault —
    // the toast below already tells them. Matches clientAssetsStore's convention.
    const isUserFault = /exceeds maximum size of|is not an accepted file type/.test(
      error?.message || ""
    );
    logger[isUserFault ? "warn" : "error"]("[ProjectBrief] File upload failed:", {
      error: error.message,
      fieldId: field.input?.id,
      companyId: props.companyId,
    });
    showToast(`Upload failed: ${error.message}`, "danger");
    event.target.value = "";
  }
};

const MAX_FIELD_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB total per field

const getFieldTotalSize = (field) => {
  const v = field.input.value;
  const files = Array.isArray(v) ? v : v ? [v] : [];
  return files.reduce((sum, f) => sum + (f?.size || 0), 0);
};

// Helper function to get file count for a field
const getFileCount = (field) => {
  if (!field.input.value) return 0;
  if (Array.isArray(field.input.value)) return field.input.value.length;
  if (field.input.value.downloadURL || field.input.value.storagePath) return 1;
  return 0;
};

// Function to handle file downloads.
//
// The stored `downloadURL` is only permanent for legacy Firebase uploads. Bunny
// Storage URLs are signed with a TTL, so a brief opened weeks after upload has a
// dead link. Re-sign from `storagePath` (written alongside it at upload) and keep
// the stored URL as the fallback so legacy attachments still work.
const downloadFile = async (file) => {
  if (file.downloadURL || file.storagePath || file.assetId) {
    try {
      // Video brief-attachments have an assetId but no storagePath, so key the
      // re-sign off either pointer — keying off storagePath alone dead-ended
      // them on the expired stored URL.
      const isBunny = file.assetId || file.storagePath;
      const url = isBunny
        ? await refreshBunnyUrl({
            path: file.storagePath,
            assetId: file.assetId,
            intent: BUNNY_URL_INTENT.DOWNLOAD,
            fallback: file.downloadURL,
          })
        : file.downloadURL;
      if (!url) throw new Error('Could not resolve a download URL');

      if (isBunny) {
        // Anchor straight at the signed URL — cdn.vysual.com sends no CORS
        // header, so fetch()-to-blob throws "Failed to fetch". The signed URL
        // already carries download=true&filename=, which drives the pull-zone
        // Edge Rule that sets Content-Disposition (link.download is ignored
        // cross-origin).
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => document.body.removeChild(link), 100);
      } else {
        // Legacy Firebase URLs: CORS-enabled and no Content-Disposition, so
        // keep forcing the download through a blob.
        const response = await fetch(url);
        const blob = await response.blob();

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      logger.error("[ProjectBrief] File download failed:", {
        error: error.message,
        fileName: file.name,
      });
      showToast(`Download failed: ${error.message}`, "danger");
    }
  }
};

// Function to download all files in a field
const downloadAllFiles = async (field) => {
  if (field.input.multiple && Array.isArray(field.input.value)) {
    showToast(`Downloading ${field.input.value.length} file(s)`, "info");
    // Download each file with a small delay to prevent browser blocking
    for (let i = 0; i < field.input.value.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, i * 300)); // 300ms delay between downloads
      await downloadFile(field.input.value[i]);
    }
  } else if (field.input.value && (field.input.value.downloadURL || field.input.value.storagePath || field.input.value.assetId)) {
    // Single file
    await downloadFile(field.input.value);
  }
};

// Function to remove uploaded file. Emit-only: the backing asset is deleted
// server-side (onBriefAttachmentRemoved) once the SAVED brief no longer
// references it. Deleting Bunny bytes here destroyed files for removals that
// were never persisted (client edit-request mode holds changes locally until
// submit), leaving the brief pointing at dead downloads.
// NEVER write back through `field.input.value` here. `field` belongs to the
// parent's brief, which is held in a shallowRef (useProjectBrief) — a nested
// mutation doesn't trigger a re-render, AND it makes the parent's
// stringify-compare in handleBriefUpdate see "no change", so the emitted brief
// is discarded too. Compute the new value and let updateFieldValue emit it.
const removeFile = (section, field, fileIndex = null) => {
  const files = field.input.value;
  let newValue;

  if (field.input.multiple && Array.isArray(files)) {
    newValue = fileIndex !== null ? files.filter((_, index) => index !== fileIndex) : [];
  } else {
    newValue = null;
  }

  // Update the brief data
  updateFieldValue(section, field, newValue);
  showToast("File removed", "success");
};

// Method to emit brief updates when field values change
// emitBriefUpdate removed - brief updates handled via updateFieldValue

// Toast helper
const showToast = (message, theme = "success") => {
  if (theme === "danger") {
    toast.error(message);
  } else if (theme === "warning") {
    toast.warning(message);
  } else if (theme === "info") {
    toast.info(message);
  } else {
    toast.success(message);
  }
};

// Method to download EDL timeline with selected assets
const downloadXML = () => {
  downloadEDLFile(selectedAssets.value, showToast);
};

const isDownloadingAssets = ref(false);

const downloadAssets = async () => {
  if (!selectedAssets.value.length || isDownloadingAssets.value) return;

  isDownloadingAssets.value = true;
  try {
    const { successCount, failCount } = await downloadAssetsSequentially(
      selectedAssets.value,
      clientAssetsStore.getAssetSource,
      "[ProjectBrief]"
    );

    if (!failCount) {
      showToast(`Downloaded ${successCount} ${successCount === 1 ? "asset" : "assets"}`, "success");
    } else if (!successCount) {
      showToast("Failed to download assets. Please try again.", "danger");
    } else {
      showToast(
        `Downloaded ${successCount} of ${selectedAssets.value.length} assets (${failCount} failed)`,
        "warning"
      );
    }
  } finally {
    isDownloadingAssets.value = false;
  }
};

// Get field value from local state or fall back to prop
const getFieldValue = (section, field) => {
  const fieldKey = `${section.name}-${field.id}-${
    field.input?.id || "no-input-id"
  }`;
  const defaultValue = field.type === "checkbox" ? [] : "";
  return localFieldValues.value.has(fieldKey)
    ? localFieldValues.value.get(fieldKey)
    : field.input?.value || defaultValue;
};

// Update field value and emit change
const updateFieldValue = (section, field, value) => {
  // Update local state immediately
  const fieldKey = `${section.name}-${field.id}-${
    field.input?.id || "no-input-id"
  }`;
  localFieldValues.value.set(fieldKey, value);

  // Get the original brief data (not the computed one with defaults)
  const originalBrief = props.brief || [];

  const updatedBrief = originalBrief.map((s) => {
    if (s === section) {
      return {
        ...s,
        fields: s.fields.map((f) => {
          // Use input.id for comparison since that's what we have
          if (f.input?.id === field.input?.id) {
            return {
              ...f,
              input: {
                ...f.input,
                value: value,
              },
            };
          }
          return f;
        }),
      };
    }
    return s;
  });

  emit("update:brief", updatedBrief);
};

// Update checkbox field value (add/remove from array)
const updateCheckboxValue = (section, field, optionValue, isChecked) => {
  // Get current field value (should be an array for checkboxes)
  const fieldKey = `${section.name}-${field.id}-${
    field.input?.id || "no-input-id"
  }`;
  const currentValue = localFieldValues.value.has(fieldKey)
    ? localFieldValues.value.get(fieldKey)
    : field.input?.value || [];

  // Ensure currentValue is an array
  const currentArray = Array.isArray(currentValue) ? currentValue : [];

  // Add or remove the value
  let newValue;
  if (isChecked) {
    // Add to array if not already present
    newValue = currentArray.includes(optionValue)
      ? currentArray
      : [...currentArray, optionValue];
  } else {
    // Remove from array
    newValue = currentArray.filter((val) => val !== optionValue);
  }

  // Update using the regular updateFieldValue method
  updateFieldValue(section, field, newValue);
};

/**
 * Shows a success toast message
 */
const showSavedToast = () => {
  toast.success("Project Brief Saved");
};

/**
 * Updates a specific field in the brief
 * @param {number} sectionIndex - Index of the section
 * @param {number} fieldIndex - Index of the field
 * @param {any} value - New value for the field
 */
const updateBrief = (sectionIndex, fieldIndex, value) => {
  // Get the original brief data (not the computed one with defaults)
  const originalBrief = props.brief || [];

  const updatedBrief = originalBrief.map((section, sIdx) => {
    if (sIdx === sectionIndex && section.fields) {
      return {
        ...section,
        fields: section.fields.map((field, fIdx) => {
          if (fIdx === fieldIndex) {
            return {
              ...field,
              input: {
                ...field.input,
                value: value,
              },
            };
          }
          return field;
        }),
      };
    }
    return section;
  });

  emit("update:brief", updatedBrief);
};

// Initialize default values when component is mounted
onMounted(() => {
  logger.debug("[ProjectBrief] Component mounted", {
    briefLength: props.brief?.length || 0,
    companyId: props.companyId,
    projectId: props.projectId,
  });

  // Check if any radio buttons need default values and emit them
  const brief = props.brief || [];
  let hasDefaults = false;

  const updatedBrief = brief.map((section) => ({
    ...section,
    fields:
      section.fields?.map((field) => {
        if (field.type === "radio" && field.input?.options?.length > 0) {
          // If no value is set, use the defaultValue or the first option as default
          if (
            field.input.value === undefined ||
            field.input.value === null ||
            field.input.value === ""
          ) {
            // First check if there's a defaultValue property
            if (
              field.input.defaultValue !== undefined &&
              field.input.defaultValue !== null
            ) {
              hasDefaults = true;
              return {
                ...field,
                input: {
                  ...field.input,
                  value: field.input.defaultValue,
                },
              };
            }
            // Otherwise, look for an option marked as default
            const defaultOption = field.input.options.find(
              (opt) => opt.isDefault === true
            );
            if (defaultOption && defaultOption.input?.value !== undefined) {
              hasDefaults = true;
              return {
                ...field,
                input: {
                  ...field.input,
                  value: defaultOption.input.value,
                },
              };
            }
            // Finally, fall back to the first option as default
            const firstOptionValue = field.input.options[0]?.input?.value;
            if (firstOptionValue !== undefined) {
              hasDefaults = true;
              return {
                ...field,
                input: {
                  ...field.input,
                  value: firstOptionValue,
                },
              };
            }
          }
        }
        return field;
      }) || [],
  }));

  // Only emit if we actually set some defaults
  if (hasDefaults) {
    emit("update:brief", updatedBrief);
  }

  // Check for asset-selection section and load assets immediately
  const assetSection = brief.find((section) => {
    return (
      section.type === "asset-selection" ||
      section._custom?.value?.type === "asset-selection"
    );
  });

  if (assetSection) {
    let selectedAssetIds = [];
    if (assetSection.selectedAssetIds) {
      selectedAssetIds = assetSection.selectedAssetIds;
    } else if (assetSection._custom?.value?.selectedAssetIds) {
      selectedAssetIds = assetSection._custom.value.selectedAssetIds;
    }

    if (selectedAssetIds.length > 0 && props.companyId) {
      logger.debug("[ProjectBrief] Loading assets immediately on mount");
      loadAssetsFromIds(selectedAssetIds);
    }
  }
});

// Expose functions to parent components
defineExpose({
  showSavedToast,
  updateBrief,
});
</script>

<style scoped lang="scss">
// Collapsible "See example" help text beneath a field
.brief-help-example {
  margin-top: 0.5rem;

  summary {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
    width: fit-content;

    &:hover {
      color: var(--bulma-text-strong);
    }
  }

  .help {
    margin-top: 0.375rem;
  }
}

// Section description styling
.section-description {
  border-bottom: 1px solid #dbdbdb;
  padding-bottom: 0.75rem;
  margin-bottom: 1.5rem;
}

.is-admin-only {
  padding: 1.5rem;
  background-color: var(--bulma-warning-light);
  border-radius: 4px;

  &.is-hidden {
    display: none;
  }
}

@media print {
  .print-hidden {
    display: none !important;
  }
  .print-show {
    display: block !important;
  }
}

// Asset selection specific styling
.selected-assets-grid {
  display: grid;
  // Upper bound matches Bunny's preview.webp width (320px) so the animated
  // hover preview never upscales on wide screens.
  grid-template-columns: repeat(auto-fill, minmax(280px, 320px));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media screen and (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 320px));
    gap: 1.25rem;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 320px));
    gap: 1rem;
  }

  @media screen and (max-width: 480px) {
    // Two-column layout on phones: each column is well below 320px on a
    // 480px screen, no upscaling risk so leave 1fr to fill the row.
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .selected-asset-item {
    min-height: 200px;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }

    position: relative;
  }
}

// Filter sidebar styling
.filter-sidebar {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  word-wrap: break-word;
}

.type-filter-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
  gap: 0.5rem;
}

.type-card {
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #dbdbdb;
  background-color: white;
  padding: 0.75rem 0.5rem;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b5b5b5;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.is-active {
    background-color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), 94%);
    border-color: var(--bulma-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .type-label {
      font-weight: 600;
      color: var(--bulma-primary) !important;
    }
  }
}

.type-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.type-icon {
  font-size: 1rem;
  transition: color 0.2s ease;
}

.type-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--bulma-text);
  transition: all 0.2s ease;
}

.type-option {
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;

  &.is-active,
  &:hover {
    background-color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), 94%);
    font-weight: 600;
  }
}

// Aspect ratio filter cards (grouped)
.aspect-ratio-filter-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(70px, 1fr));
  gap: 0.5rem;
}

.aspect-ratio-card {
  cursor: pointer;
  border-radius: 4px;
  border: 1px solid #dbdbdb;
  background-color: white;
  padding: 0.75rem 0.5rem;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: #b5b5b5;
  }

  &.is-active {
    background-color: hsl(var(--bulma-primary-h), var(--bulma-primary-s), 94%);
    border-color: var(--bulma-primary);

    .aspect-ratio-label {
      font-weight: 600;
      color: var(--bulma-primary) !important;
    }
  }
}

.aspect-ratio-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.aspect-ratio-icon {
  font-size: 1rem;
}

.aspect-ratio-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--bulma-text);
}

// Modal styling - Full screen modal
.asset-selection-modal {
  .modal-card.is-fullscreen {
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    margin: 0;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-card-body {
    padding: 0;
    flex: 1;
    overflow: hidden;
    overflow-x: hidden;
    display: flex;

    .columns.is-gapless {
      flex: 1;
      height: 100%;
      margin: 0;
      display: flex;

      .column.is-narrow {
        flex: 0 0 350px;
        width: 350px;
        min-width: 350px;
        max-width: 350px;
        border-right: 1px solid #dbdbdb;
        background-color: #fafafa;
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 100%;
      }

      .column:not(.is-narrow) {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        min-width: 0;

        .content {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          max-height: 100%;
        }
      }
    }
  }

  .modal-card-foot {
    flex: 0 0 auto;
    border-top: 1px solid #dbdbdb;
  }
}

// Asset selection grid styling
.asset-selection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media screen and (max-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.25rem;
  }

  @media screen and (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }
}

.asset-selection-item {
  transition: transform 0.2s ease;
  min-height: 200px;

  &:hover {
    transform: translateY(-2px);
  }

  // Ensure MediaCard takes full width
  :deep(.media-card) {
    height: 100%;
    display: flex;
    flex-direction: column;

    .card {
      height: 100%;
      display: flex;
      flex-direction: column;

      .card-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }
  }
}

// Responsive breakpoints for sidebar
@media screen and (max-width: 1200px) {
  .asset-selection-modal
    .modal-card-body
    .columns.is-gapless
    .column.is-narrow {
    flex: 0 0 320px;
    width: 320px;
    min-width: 320px;
    max-width: 320px;
  }
}

@media screen and (max-width: 1024px) {
  .asset-selection-modal
    .modal-card-body
    .columns.is-gapless
    .column.is-narrow {
    flex: 0 0 300px;
    width: 300px;
    min-width: 300px;
    max-width: 300px;
  }
}

@media screen and (max-width: 768px) {
  .asset-selection-modal .modal-card-body .columns.is-gapless {
    flex-direction: column;

    .column.is-narrow {
      flex: 0 0 40%;
      width: 100%;
      min-width: 100%;
      max-width: 100%;
      height: 40%;
      border-right: none;
      border-bottom: 1px solid #dbdbdb;
    }

    .column:not(.is-narrow) {
      flex: 1;
      height: 60%;
      min-height: 60%;
    }
  }

  .type-filter-cards {
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
    gap: 0.4rem;
  }

  .type-card {
    padding: 0.5rem 0.25rem;

    .type-icon {
      font-size: 0.875rem;
    }

    .type-label {
      font-size: 0.6875rem;
    }
  }
}

// General styling improvements
.transition {
  transition: all 0.2s ease;
}

// Empty states
.filtered-empty-state {
  .icon {
    opacity: 0.6;
  }
}

// Uploaded files styling
.uploaded-files {
  .uploaded-file-item {
    padding: 0.5rem;
    border-radius: 4px;
    background-color: white;
    border: 1px solid #dbdbdb;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
    }

    .level {
      margin-bottom: 0;
    }
  }

  .notification.is-primary.is-light {
    border-left: 4px solid var(--bulma-info);
  }
}

// Notification improvements
.notification.is-primary.is-light {
  border-left: 4px solid var(--bulma-primary);
}

// Subtle info bar shown when client-uploaded media is gated by contract
.client-media-restriction-notice {
  background-color: hsl(207, 61%, 96%);
  color: hsl(207, 61%, 28%);
  border-left: 4px solid var(--bulma-info);
}

// Scroll styling for filter sidebar
.filter-sidebar::-webkit-scrollbar {
  width: 6px;
  height: 0px;
}

.filter-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.filter-sidebar::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;

  &:hover {
    background: #a0aec0;
  }
}

.filter-sidebar::-webkit-scrollbar-horizontal {
  display: none;
}

// Pending changes indicator
.has-pending-changes {
  border-left: 3px solid hsl(48, 100%, 67%);
  padding-left: 1rem;
  background-color: hsla(48, 100%, 67%, 0.05);
  border-radius: 4px;

  .control {
    opacity: 0.7;
  }
}
</style>
